// Standalone Nav + Footer chrome extracted from _v07_swiss.jsx so they
// can be mounted on every interior page of the portal — not just the
// homepage. Visual treatment is byte-identical to the variant, with all
// dynamic copy bindings (t/ts/useDomainCopy) collapsed to their default
// strings. The only configurable input is `brand`.
import React, { useState, useEffect } from 'react';
import { Link } from '../_shared.jsx';

const theme = {
  bg: '#fafaf7', fg: '#0a0a0a', muted: '#6a6a6a',
  border: '#0a0a0a', accent: '#d62828', accentFg: '#ffffff',
  inputBg: '#ffffff', radius: 0,
  font: '"Inter", "Neue Haas Grotesk", "Helvetica Neue", Helvetica, sans-serif',
  mono: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
  serif: '"Source Serif Pro", "IBM Plex Mono", Georgia, serif',
  dark: '#0a0a0a',
  rule: '#0a0a0a',
  ledgerBg: '#f3f2ec',
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update); };
  }, []);
  return isMobile;
};

const HamburgerIcon = ({ open }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? <><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></> :
            <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
  </svg>
);

const grid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(12, 1fr)',
  gap: 20,
  padding: '0 32px',
};

const navItems = [
  { label: '01 Photovoltaik', href: '/photovoltaik' },
  { label: '02 Wärmepumpe',   href: '/wärmepumpe' },
  { label: '03 Förderung',    href: '/förderung-finder' },
  { label: '04 Beratung',     href: '/kontakt' },
];

export function V07SwissNav({ brand = 'Waemo', brandLogo = null }) {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const brandLabel = brand || 'Waemo';
  const brandSuffix = 'Energie';
  if (isMobile) {
    return (
      <div style={{
        padding: '12px 16px',
        borderBottom: `1px solid ${theme.fg}`,
        background: theme.bg, color: theme.fg, fontFamily: theme.font,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        position: 'relative',
      }}>
        <Link href="/" style={{ display: 'flex', alignItems: 'center', fontWeight: 700, fontSize: 15, letterSpacing: '-0.02em', color: theme.fg, textDecoration: 'none' }} aria-label={brandLabel + ' Startseite'}>
          {brandLogo
            ? <img src={brandLogo} alt={brandLabel + ' Logo'} width="120" height="32" style={{ height: 32, width: 'auto', maxWidth: 160, display: 'block' }} />
            : <>{brandLabel}<span style={{ color: theme.accent }}>—</span>{brandSuffix}</>
          }
        </Link>
        <button
          aria-label="Menü"
          onClick={() => setIsMenuOpen(o => !o)}
          style={{ background: 'transparent', border: 'none', color: theme.fg, cursor: 'pointer', padding: 4, display: 'flex' }}
        >
          <HamburgerIcon open={isMenuOpen} />
        </button>
        {isMenuOpen && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0,
            background: theme.bg, borderBottom: `1px solid ${theme.fg}`,
            padding: '16px', display: 'flex', flexDirection: 'column', gap: 12,
            zIndex: 50,
          }}>
            {navItems.map(l => (
              <Link key={l.label} href={l.href} style={{
                color: theme.muted, textDecoration: 'none',
                fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>{l.label}</Link>
            ))}
            <span style={{ fontSize: 11, color: theme.muted, letterSpacing: '0.06em', marginTop: 6 }}>{'DE · EN · FR'}</span>
          </div>
        )}
      </div>
    );
  }
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 20, padding: '14px 32px',
      borderBottom: `1px solid ${theme.fg}`, alignItems: 'baseline',
      background: theme.bg, color: theme.fg, fontFamily: theme.font,
    }}>
      <Link href="/" style={{ gridColumn: 'span 3', display: 'flex', alignItems: 'center', fontWeight: 700, fontSize: 15, letterSpacing: '-0.02em', color: theme.fg, textDecoration: 'none' }} aria-label={brandLabel + ' Startseite'}>
        {brandLogo
          ? <img src={brandLogo} alt={brandLabel + ' Logo'} width="120" height="32" style={{ height: 32, width: 'auto', maxWidth: 160, display: 'block' }} />
          : <>{brandLabel}<span style={{ color: theme.accent }}>—</span>{brandSuffix}</>
        }
      </Link>
      <div style={{ gridColumn: 'span 6', fontSize: 11, color: theme.muted, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'flex', gap: 18, alignItems: 'baseline' }}>
        <Link href="/photovoltaik" style={{ color: theme.muted, textDecoration: 'none' }}>{'01 Photovoltaik'}</Link>
        <span aria-hidden>·</span>
        <Link href="/wärmepumpe" style={{ color: theme.muted, textDecoration: 'none' }}>{'02 Wärmepumpe'}</Link>
        <span aria-hidden>·</span>
        <Link href="/förderung-finder" style={{ color: theme.muted, textDecoration: 'none' }}>{'03 Förderung'}</Link>
        <span aria-hidden>·</span>
        <Link href="/kontakt" style={{ color: theme.muted, textDecoration: 'none' }}>{'04 Beratung'}</Link>
      </div>
      <div style={{ gridColumn: 'span 3', textAlign: 'right', fontSize: 11, color: theme.muted, letterSpacing: '0.06em' }}>{'DE · EN · FR'}</div>
    </div>
  );
}

export function V07SwissFooter({ brand = 'Waemo', domain = null, brandLogo = null }) {
  const isMobile = useIsMobile();
  const brandLabel = brand || 'Waemo';
  const brandSuffix = 'Energie';
  const contactEmail = `info@${domain?.name || (brand ? brand.toLowerCase() + '.de' : 'scienceworks.com')}`;
  if (isMobile) {
    return (
      <footer style={{
        borderTop: `2px solid ${theme.fg}`, background: theme.bg, padding: '24px 16px',
        color: theme.fg, fontFamily: theme.font,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: theme.mono, fontSize: 11, letterSpacing: '0.04em' }}>
          {brandLogo
            ? <img src={brandLogo} alt={brandLabel + ' Logo'} width="120" height="32" style={{ height: 32, width: 'auto', maxWidth: 160, display: 'block' }} />
            : <div style={{ fontWeight: 600, color: theme.fg }}>{brandLabel}&nbsp;—&nbsp;{brandSuffix}</div>
          }
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, color: theme.muted }}>
            <span>{'Inhalt:'}</span>
            <Link href="/impressum" style={{ color: 'inherit', textDecoration: 'none' }}>{'Impressum'}</Link>
            <Link href="/datenschutz" style={{ color: 'inherit', textDecoration: 'none' }}>{'Datenschutz'}</Link>
            <Link href="/agb" style={{ color: 'inherit', textDecoration: 'none' }}>{'AGB'}</Link>
            <Link href="/widerruf" style={{ color: 'inherit', textDecoration: 'none' }}>{'Widerruf'}</Link>
          </div>
          <div style={{ color: theme.muted, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span>{'Kontakt:'}</span>
            <a href={`mailto:${contactEmail}`} style={{ color: 'inherit', textDecoration: 'none' }}>{contactEmail}</a>
            <span>{'Hannover · bundesweit'}</span>
          </div>
          <div>{`© ${new Date().getFullYear()}`}</div>
          <div style={{
            textAlign: 'center', fontSize: 10, fontStyle: 'italic',
            color: theme.muted, letterSpacing: '0.32em', textTransform: 'uppercase',
            marginTop: 8,
          }}>
            {'DE · EN · FR · ISSN 1866-2001'}
          </div>
        </div>
      </footer>
    );
  }
  return (
    <footer style={{
      borderTop: `2px solid ${theme.fg}`, background: theme.bg, padding: '48px 0 28px',
      color: theme.fg, fontFamily: theme.font,
    }}>
      <div style={{ ...grid, alignItems: 'baseline' }}>
        <div style={{ gridColumn: '1 / span 3', fontFamily: theme.mono, fontSize: 12, letterSpacing: '0.04em', fontWeight: 600 }}>
          {brandLogo
            ? <img src={brandLogo} alt={brandLabel + ' Logo'} width="120" height="32" style={{ height: 32, width: 'auto', maxWidth: 160, display: 'block' }} />
            : <>{brandLabel}&nbsp;—&nbsp;{brandSuffix}</>
          }
        </div>
        <div style={{ gridColumn: '4 / span 4', fontFamily: theme.mono, fontSize: 12, letterSpacing: '0.04em', color: theme.muted, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <span>{'Inhalt:'}</span>
          <Link href="/impressum" style={{ color: 'inherit', textDecoration: 'none' }}>{'Impressum'}</Link>
          <span aria-hidden>/</span>
          <Link href="/datenschutz" style={{ color: 'inherit', textDecoration: 'none' }}>{'Datenschutz'}</Link>
          <span aria-hidden>/</span>
          <Link href="/agb" style={{ color: 'inherit', textDecoration: 'none' }}>{'AGB'}</Link>
          <span aria-hidden>/</span>
          <Link href="/widerruf" style={{ color: 'inherit', textDecoration: 'none' }}>{'Widerruf'}</Link>
        </div>
        <div style={{ gridColumn: '8 / span 3', fontFamily: theme.mono, fontSize: 12, letterSpacing: '0.04em', color: theme.muted }}>
          <span>{'Kontakt:'} </span>
          <a href={`mailto:${contactEmail}`} style={{ color: 'inherit', textDecoration: 'none' }}>{contactEmail}</a>
          <span> · </span>
          <span>{'Hannover · bundesweit'}</span>
        </div>
        <div style={{ gridColumn: '11 / span 2', fontFamily: theme.mono, fontSize: 12, letterSpacing: '0.04em', textAlign: 'right' }}>
          {`© ${new Date().getFullYear()}`}
        </div>
      </div>

      <div style={{ ...grid, marginTop: 32 }}>
        <div style={{
          gridColumn: '1 / span 12',
          textAlign: 'center',
          fontFamily: theme.mono,
          fontSize: 10, fontStyle: 'italic',
          color: theme.muted,
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
        }}>
          {'DE · EN · FR · ISSN 1866-2001'}
        </div>
      </div>
    </footer>
  );
}
